/*!
 * @license
 * Copyright 2019 Alfresco Software, Ltd.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { AppStore, SnackbarErrorAction } from '@alfresco/aca-shared/store';
import { TranslationService } from '@alfresco/adf-core';
import { Component, Inject, OnInit, inject, SecurityContext } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Store } from '@ngrx/store';
import { SMTConfirmDialogComponent } from 'projects/alden-extension/src/lib/dialogs/confirm/confirm.dialog';
import { DateType } from 'projects/alden-extension/src/lib/models/date-types';
import { UdeuLoan } from 'projects/deusto-extension/src/lib/model/classes/udeu-loan';
import { UdeuStatus } from 'projects/deusto-extension/src/lib/model/classes/udeu-status';
import { UDEU_LOAN_RENEW_APPROVED_STATUS, UDEU_LOAN_RENEW_REJECTED_STATUS, UDEU_LOAN_RENEW_REQUEST_STATUS } from 'projects/deusto-extension/src/lib/model/contants/deusto';
import { RENEW_DATE_FIELD, RENEW_OBSERVATIONS_FIELD, RETURN_DATE_FIELD } from 'projects/deusto-extension/src/lib/model/contants/udeu-loan';
import { LoanService } from 'projects/deusto-extension/src/lib/services/loan.service';
import { UdeuService } from 'projects/deusto-extension/src/lib/services/udeu.service';

export interface DialogData {
    loanId: string;
    validate: boolean;
    reject: boolean;
}

@Component({
    templateUrl: './renew.dialog.html',
    styleUrls: ['./renew.dialog.scss'],
})
export class LoanRenewDialogComponent implements OnInit {

    private _formBuilder = inject(FormBuilder);
    formGroup: FormGroup<any>;

    loading: boolean;

    loan: UdeuLoan;

    minDate: Date;
    //maxDate: Date;
    DateType: typeof DateType = DateType;

    constructor(@Inject(MAT_DIALOG_DATA) public data: DialogData, private dialogRef: MatDialogRef<LoanRenewDialogComponent>, private dialog: MatDialog, private udeuService: UdeuService,
        private loanService: LoanService, protected store: Store<AppStore>, private sanitizer: DomSanitizer, private translationService: TranslationService) { }

    async ngOnInit(): Promise<void> {
        this.loading = true;

        let returnDate;
        let renewDate;
        await this.loanService.getLoan(this.data.loanId).then((loan) => {
            this.loan = loan;
            returnDate = this.loan.info.returnDate;
            renewDate = this.loan.info.renewDate;
            if (returnDate) {
                if (this.data.validate) {
                    this.minDate = new Date(returnDate);
                    this.minDate.setDate(this.minDate.getDate() + 1);
                } else {
                    this.minDate = new Date(renewDate ?? returnDate);
                    this.minDate.setDate(this.minDate.getDate() + 1);

                 /*   this.maxDate = new Date(renewDate ?? returnDate);
                    this.maxDate.setDate(this.maxDate.getDate() + 15); */
                }
            }
        });

        this.formGroup = this._formBuilder.group({
            [RETURN_DATE_FIELD]: [{ value: returnDate, disabled: true }, [Validators.required]],
            "oldRenewDate": [{ value: renewDate, disabled: true }],
            [RENEW_DATE_FIELD]: [this.minDate],
            'oldRenewObservations': [{ value: this.loan.info.renewObservations, disabled: true }],
            [RENEW_OBSERVATIONS_FIELD]: [''],
            'finalReturnDate': [renewDate],
        });

        this.data.validate ? this.formGroup.get('finalReturnDate').addValidators(Validators.required) : this.formGroup.get(RENEW_DATE_FIELD).addValidators(Validators.required);
        this.data.reject ? this.formGroup.disable() : null;

        this.loading = false;
    }

    sanitizedHtmlContent(): string {
        return this.sanitizer.sanitize(SecurityContext.HTML, this.translationService.instant(this.data.validate ? (!this.data.reject ? 'DEUSTO.DIALOGS.ACCEPT_RENEW_LOAN.MESSAGE' : 'DEUSTO.DIALOGS.REJECT_RENEW_LOAN.MESSAGE') : 'DEUSTO.DIALOGS.RENEW_LOAN.MESSAGE', { loanName: this.loan.name }));
    }

    renewLoan() {
        const status = this.data.validate ? (!this.data.reject ? UDEU_LOAN_RENEW_APPROVED_STATUS : UDEU_LOAN_RENEW_REJECTED_STATUS) : UDEU_LOAN_RENEW_REQUEST_STATUS;

        // Solicitar renovación
        let title = 'DEUSTO.DIALOGS.RENEW_LOAN.TITLE';
        let formField = false;
        let optionalFormField = false;
    
        if(UDEU_LOAN_RENEW_APPROVED_STATUS === status) {
            // Acpetar renovación
            title = 'DEUSTO.DIALOGS.ACCEPT_RENEW_LOAN.TITLE';
        } else if(UDEU_LOAN_RENEW_REJECTED_STATUS === status) {
            // Rechazar renovación
            title = 'DEUSTO.DIALOGS.REJECT_RENEW_LOAN.TITLE';
            formField = true,
            optionalFormField = true;
        }

        const dialogRef = this.dialog.open(SMTConfirmDialogComponent, {
            data: { title, formField, optionalFormField },
            minWidth: '250px'
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {   
                const udeuLoan = new UdeuLoan(this.data.loanId);
                udeuLoan.status = new UdeuStatus(status, !this.data.reject ? this.formGroup.get(RENEW_OBSERVATIONS_FIELD).value : (result !== true ? result : ''));
                this.data.validate ? udeuLoan.info.returnDate = this.formGroup.get('finalReturnDate').value : udeuLoan.info.renewDate = this.formGroup.get(RENEW_DATE_FIELD).value;
                this.loanService.loanRenew(udeuLoan).then(() => {
                    this.dialogRef.close(true);
                    this.udeuService.reloadCustomListTable();
                }).catch((err) => {
                    console.error("ERROR", err);
                    this.store.dispatch(new SnackbarErrorAction('No se pudo renovar el préstamo.'));
                });
            }
        });
    }

}